import React from 'react';
import { UserRoles, IItemForGetShortDishInfo } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import DraggableSelectedItem from 'components/SelectedItem/DraggableSelectedItem';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import style from './DnDList.module.scss';

interface IProps {
	setAdditionalDishes: (value: string[]) => void;
	removeAdditionalDish: (value: string) => void;
	selectedAdditionalDishes: string[];
	fetchedAdditionalDishes: IItemForGetShortDishInfo[];
}

export const DnDList: React.FC<IProps> = React.memo(
	({ setAdditionalDishes, removeAdditionalDish, selectedAdditionalDishes, fetchedAdditionalDishes }) => {
		const hasChangePositionAccess = useRoleAccess([
			UserRoles.SUPER_USER,
			UserRoles.CLIENT_MANAGER,
			UserRoles.GROUP_MANAGER,
			UserRoles.TECHNICAL,
		]);

		const handleDragEnd = (result: DropResult) => {
			if (!selectedAdditionalDishes.length || !result.destination) {
				return null;
			}
			const items = selectedAdditionalDishes;
			const [reorderedItem] = items.splice(result.source.index, 1);
			items.splice(result.destination.index, 0, reorderedItem);
			setAdditionalDishes(items);
		};

		return (
			<DragDropContext onDragEnd={handleDragEnd}>
				<Droppable droppableId="suggestedDishList">
					{(provided) => (
						<div className={style.suggestedDishList} ref={provided.innerRef} {...provided.droppableProps}>
							{selectedAdditionalDishes.map((el, index) => {
								const dish = fetchedAdditionalDishes.find((fetched) => fetched.id === el);

								return (
									<Draggable key={el} draggableId={el} index={index}>
										{(provided) => (
											<DraggableSelectedItem
												draggableInnerRef={provided.innerRef}
												provided={provided}
												positionNumber={index + 1}
												label={dish?.name || ''}
												onDelete={() => removeAdditionalDish(el)}
												hasDragAccess={hasChangePositionAccess}
												additionalContent={
													<>
														{dish?.externalId && (
															<ChunkOfTextWithCopying
																text={dish.externalId}
																className={style.externalId}
															/>
														)}
													</>
												}
											/>
										)}
									</Draggable>
								);
							})}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
			</DragDropContext>
		);
	},
);
